/**
 * /portal/commission — the only commission flow.
 *
 * Replaces the legacy two-step discover-then-commission pattern
 * (`/portal/commission/discover` → `/portal/commission` form) with a
 * single page: scope, live price, attest, pay. The buyer is
 * authenticated by definition (the route lives inside `PortalShell`)
 * so we read contact info from the auth context and never ask for it
 * again.
 *
 * State is owned here via `useReducer` and threaded into each
 * section component as `{state, dispatch}`. Async calls run as side
 * effects in this file (close to the dispatcher), not inside the
 * section components, so cancellation + race-condition handling stays
 * centralized.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useClientAuth } from "@/auth/client-auth-context";

import {
  ApiError,
  commissionEngagement,
  fetchCurrentAttestation,
  previewCartPricing,
  runDiscovery,
  type CommissionSubmission,
} from "./api";
import {
  buildCartFromState,
  canSubmit,
  initialReconConfiguratorState,
  reconConfiguratorReducer,
} from "./state";
import { totalSelectedSubdomains } from "./selectors";
import { PrimaryScopeCard } from "./components/PrimaryScopeCard";
import { SubdomainGrid } from "./components/SubdomainGrid";
import { AdditionalAssetList } from "./components/AdditionalAssetList";
import { AttestationSection } from "./components/AttestationSection";
import { OrderSummary } from "./components/OrderSummary";
import { WhatsIncluded } from "./components/WhatsIncluded";

import "./a11y.css";
import "./commission-page.css";

// --------------------------------------------------------------------------- //
// Effect helpers                                                              //
// --------------------------------------------------------------------------- //

function nextRequestId(): number {
  return Date.now();
}

function useDebouncedRef() {
  const ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const set = (fn: () => void, ms: number) => {
    if (ref.current) clearTimeout(ref.current);
    ref.current = setTimeout(fn, ms);
  };
  const cancel = () => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  };
  useEffect(() => () => cancel(), []);
  return { set, cancel };
}

// --------------------------------------------------------------------------- //
// Component                                                                   //
// --------------------------------------------------------------------------- //

export function CommissionPage() {
  const navigate = useNavigate();
  const { me, isBootstrapping } = useClientAuth();

  const [state, dispatch] = useReducer(
    reconConfiguratorReducer,
    initialReconConfiguratorState,
  );

  // ---- Mirror the authenticated client into the configurator state ----
  // The configurator's state model carries contact fields because the
  // attestation needs a name to match against. The buyer never sees
  // these fields — they're invisible plumbing. We sync from `me` so
  // attestation match and submission payloads have what they need.
  useEffect(() => {
    if (!me) return;
    dispatch({ type: "set_contact_field", field: "name", value: me.contact_name || "" });
    dispatch({
      type: "set_contact_field",
      field: "email",
      value: me.contact_email || "",
    });
    dispatch({
      type: "set_contact_field",
      field: "company",
      value: me.company || "",
    });
    // No phone in ClientProfile — leave blank; the legacy IntakeRequest
    // accepts an empty string.
  }, [me]);

  // ---- Attestation: load on mount, retry on click ----
  const loadAttestation = useCallback(async () => {
    const requestId = nextRequestId();
    dispatch({ type: "attestation_load_start", requestId });
    try {
      const data = await fetchCurrentAttestation();
      dispatch({ type: "attestation_load_ok", requestId, data });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Could not load the authorization text.";
      dispatch({ type: "attestation_load_error", requestId, message });
    }
  }, []);
  useEffect(() => {
    void loadAttestation();
  }, [loadAttestation]);

  // ---- Primary discovery: triggered by the buyer ----
  const primaryAbortRef = useRef<AbortController | null>(null);
  const runPrimaryDiscovery = useCallback(async () => {
    const seed = state.primaryDomain.trim();
    if (!seed) return;
    if (primaryAbortRef.current) primaryAbortRef.current.abort();
    const ctrl = new AbortController();
    primaryAbortRef.current = ctrl;

    const requestId = nextRequestId();
    dispatch({ type: "primary_discovery_start", requestId });
    try {
      const result = await runDiscovery(seed, { signal: ctrl.signal });
      dispatch({
        type: "primary_discovery_ok",
        requestId,
        assets: result.discovered_assets,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const message =
        err instanceof ApiError ? err.message : "Discovery scan failed.";
      dispatch({ type: "primary_discovery_error", requestId, message });
    }
  }, [state.primaryDomain]);

  // ---- Additional-asset discovery (one AbortController per asset) ----
  const additionalAbortRefs = useRef<Map<string, AbortController>>(new Map());
  useEffect(() => {
    const refs = additionalAbortRefs.current;
    return () => {
      for (const ctrl of refs.values()) ctrl.abort();
      refs.clear();
    };
  }, []);
  const runAdditionalDiscovery = useCallback(
    async (assetId: string, value: string) => {
      const seed = value.trim();
      if (!seed) return;
      const prev = additionalAbortRefs.current.get(assetId);
      if (prev) prev.abort();
      const ctrl = new AbortController();
      additionalAbortRefs.current.set(assetId, ctrl);

      const requestId = nextRequestId();
      dispatch({ type: "additional_discovery_start", id: assetId, requestId });
      try {
        const result = await runDiscovery(seed, { signal: ctrl.signal });
        dispatch({
          type: "additional_discovery_ok",
          id: assetId,
          requestId,
          assets: result.discovered_assets,
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message =
          err instanceof ApiError ? err.message : "Discovery scan failed.";
        dispatch({
          type: "additional_discovery_error",
          id: assetId,
          requestId,
          message,
        });
      }
    },
    [],
  );

  // ---- Live quote: debounced 250ms on cart fingerprint changes ----
  const cart = useMemo(() => buildCartFromState(state), [state]);
  const cartFingerprint = useMemo(() => JSON.stringify(cart), [cart]);
  const quoteAbortRef = useRef<AbortController | null>(null);
  const quoteDebounce = useDebouncedRef();
  useEffect(() => {
    if (cart.length === 0) return;
    quoteDebounce.set(() => {
      if (quoteAbortRef.current) quoteAbortRef.current.abort();
      const ctrl = new AbortController();
      quoteAbortRef.current = ctrl;
      const requestId = nextRequestId();
      dispatch({ type: "quote_start", requestId });
      void (async () => {
        try {
          const breakdown = await previewCartPricing(cart, {
            signal: ctrl.signal,
          });
          if (breakdown.error) {
            dispatch({ type: "quote_error", requestId, message: breakdown.error });
            return;
          }
          dispatch({ type: "quote_ok", requestId, breakdown });
        } catch (err) {
          if (err instanceof DOMException && err.name === "AbortError") return;
          const message =
            err instanceof ApiError ? err.message : "Could not compute the price.";
          dispatch({ type: "quote_error", requestId, message });
        }
      })();
    }, 250);
    return () => quoteDebounce.cancel();
    // We intentionally key on cartFingerprint to avoid re-firing when
    // unrelated state (attestation) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartFingerprint]);

  // ---- Submit: hit /client-portal/commission, redirect to Stripe ----
  const handleSubmit = useCallback(async () => {
    if (!canSubmit(state)) return;
    if (state.attestation.kind !== "ok") return;

    dispatch({ type: "submission_start" });
    try {
      const targets = buildCartFromState(state).map((c) => ({
        target_type:
          c.kind === "ip" ? ("ip_address" as const) : ("domain" as const),
        value: c.value,
        priority: "primary" as const,
      }));

      const payload: CommissionSubmission = {
        assessment_type: "business",
        company_size: "small",
        contact_name: state.contact.name.trim(),
        contact_email: state.contact.email.trim().toLowerCase(),
        contact_phone: state.contact.phone.trim() || null,
        company: state.contact.company.trim() || null,
        package_id: "quick_check",
        // Quick Check pricing is package-based (single flat rate + cart
        // add-ons); the module-toggle map is unused for this product
        // shape but the schema still requires the field. Empty object
        // is the documented "no per-module toggles" payload.
        modules: {},
        targets,
        attestation: {
          version: state.attestation.data.version,
          text_hash: state.attestation.data.text_hash,
          attested_name: state.attestedName.trim(),
        },
      };

      const result = await commissionEngagement(payload);
      dispatch({
        type: "submission_redirecting",
        checkoutUrl: result.checkout_url,
      });
      window.location.href = result.checkout_url;
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Could not start checkout. Please try again.";
      dispatch({ type: "submission_error", message });
    }
  }, [state]);

  // ---- Derived state ----
  const apex = state.primaryDomain.trim().toLowerCase();
  const primaryDiscoveryAssets =
    state.primaryDiscovery.kind === "ok" ? state.primaryDiscovery.data : [];
  const primarySelectedSet = useMemo(
    () => new Set(state.primarySelectedSubs.map((s) => s.toLowerCase())),
    [state.primarySelectedSubs],
  );
  const globalSubCount = totalSelectedSubdomains(state);

  // While auth is bootstrapping the `me` profile may be null; render a
  // neutral state to avoid a flash of "empty contact" before sync.
  if (isBootstrapping) {
    return (
      <div className="commission-page commission-page-loading">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="commission-page">
      <header className="commission-page-hero">
        <div className="eyebrow">/RECON · NEW ENGAGEMENT</div>
        <h1>Define your scope</h1>
        <p className="commission-page-hero-sub">
          A /Recon engagement maps your external attack surface — every
          public-facing asset, every misconfiguration, every exposure an
          attacker can find before you do. Pick the domains and IPs in
          scope; we'll handle the rest.
        </p>
      </header>

      <main className="commission-page-main">
        <WhatsIncluded />

        <PrimaryScopeCard
          state={state}
          dispatch={dispatch}
          onScan={runPrimaryDiscovery}
        />
        {state.primaryDiscovery.kind === "ok" && (
          <SubdomainGrid
            assets={primaryDiscoveryAssets}
            apex={apex}
            selectedSet={primarySelectedSet}
            onToggle={(hostname) =>
              dispatch({ type: "toggle_primary_sub", hostname })
            }
            onClearAll={() => dispatch({ type: "clear_primary_subs" })}
            globalSelectedCount={globalSubCount}
            variant="primary"
            idPrefix="primary"
          />
        )}

        <AdditionalAssetList
          state={state}
          dispatch={dispatch}
          onDiscoverDomain={runAdditionalDiscovery}
        />

        <AttestationSection
          state={state}
          dispatch={dispatch}
          onRetry={loadAttestation}
        />
      </main>

      <OrderSummary
        state={state}
        me={me}
        onSubmit={handleSubmit}
        onBack={() => navigate("/portal")}
      />
    </div>
  );
}
